import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useSpring, animated } from '@react-spring/web';
import { useDrag } from '@use-gesture/react';
import {
  Home, CheckCircle, Users, Plus, Bell, Settings,
  Calendar, Clock, TrendingUp, Award, Target,
  ChevronRight, Sparkles, Zap, Filter, Search,
  Sun, Moon, LogOut, X, Edit2, Trash2, AlertCircle, Repeat,
  UserPlus, Mail, Copy, Loader, Lock, Eye, EyeOff, Crown, Globe,
  MapPin, CloudRain, Wind, Thermometer, Droplets, AlertTriangle
} from 'lucide-react';
import toast from 'react-hot-toast';
import { taskService, eventService } from '../../api/taskService';
import { familyService } from '../../api/familyService';
import { useStatus } from '../../contexts/StatusContext';
import EventCard from '../Events/EventCard';
import EventCreationModal from '../Events/EventCreationModal';
import EventHierarchyView from '../Events/EventHierarchyView';
import UpcomingFeaturesBanner from './UpcomingFeaturesBanner';
import SubtaskList from '../tasks/SubtaskList';
import { locationIntelligenceService } from '../../services/LocationIntelligenceService';
import { weatherSchedulingService } from '../../services/WeatherSchedulingService';
import LocationPicker from '../location/LocationPicker';
import WeatherTaskSuggestions from '../weather/WeatherTaskSuggestions';
import WeatherAlerts from '../weather/WeatherAlerts';

const ModernDashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();

  // Get family context for API calls
  const statusContext = useStatus();
  // Get user_id from statusContext (Cognito sub UUID)
  const userId = statusContext?.user_id;

  // For family_id: if not set, we need to create or fetch family
  // For now, skip family-scoped operations if no family_id (single user mode)
  const familyId = statusContext?.family_id;

  const [activeTab, setActiveTab] = useState('home');
  const [tasks, setTasks] = useState([]);
  const [events, setEvents] = useState([]);
  const [showAddTask, setShowAddTask] = useState(false);
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showTaskDetail, setShowTaskDetail] = useState(false);
  const [showEventDetail, setShowEventDetail] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editedTask, setEditedTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    priority: 'medium',
    category: 'general',
    dueDate: '',
    location: '',
    isRecurring: false,
    frequency: 'weekly',
    recurringEndDate: '',
    task_type: 'task',
    visibility: 'family',
    private_notes: '',
    reveal_date: '',
    event_id: null,
    parent_task_id: null
  });

  // Family invite system state
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [familyCode, setFamilyCode] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [familyMembers, setFamilyMembers] = useState([]);
  const [inviteEmail, setInviteEmail] = useState('');

  // Location and Weather state
  const [userLocation, setUserLocation] = useState(null);
  const [weatherData, setWeatherData] = useState(null);
  const [weatherForecast, setWeatherForecast] = useState([]);
  const [weatherAlerts, setWeatherAlerts] = useState([]);
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [locationPermissionStatus, setLocationPermissionStatus] = useState('unknown'); // 'granted', 'denied', 'prompt', 'unknown'
  const [loadingWeather, setLoadingWeather] = useState(false);

  // Pull-to-refresh animation
  const [{ y }, api] = useSpring(() => ({ y: 0 }));
  const bind = useDrag(
    ({ down, movement: [, my] }) => {
      if (my > 100 && !down && !refreshing) {
        handleRefresh();
      }
      api.start({ y: down ? Math.max(0, my) : 0 });
    },
    { axis: 'y', bounds: { top: 0 }, rubberband: true }
  );

  // Load tasks from API
  const loadTasks = async () => {
    try {
      setLoading(true);
      console.log('[Dashboard] Loading tasks from API...', { familyId, userId });

      const fetchedTasks = await taskService.getTasks({
        family_id: familyId,
        user_id: userId
      });

      // Transform API data to match UI expectations (snake_case from database)
      const transformedTasks = fetchedTasks.map(task => ({
        id: task.task_id || task.id,
        title: task.title || 'Untitled Task',
        description: task.description || '',
        priority: (task.priority_score > 75 ? 'urgent' : task.priority_score > 50 ? 'high' : task.priority_score > 25 ? 'medium' : 'low'),
        category: (task.category || 'general').toLowerCase(),
        status: (task.status || 'pending').toLowerCase(),
        dueDate: task.due_date || 'No due date',
        assigned: task.assigned_to_name || user?.displayName || 'You',
        createdBy: task.created_by, // UUID of user who created the task
        isRecurring: task.is_recurring || false,
        frequency: task.frequency || 'weekly',
        recurringEndDate: task.recurring_end_date || ''
      }));

      setTasks(transformedTasks);
      console.log('[Dashboard] Loaded tasks:', transformedTasks.length);
    } catch (error) {
      console.error('[Dashboard] Failed to load tasks:', error);
      toast.error('Failed to load tasks. Please try again.');
      // Set empty tasks on error - no mock data in production
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  // Load events from API
  const loadEvents = async () => {
    try {
      console.log('[Dashboard] Loading events from API...', { familyId, userId });

      const fetchedEvents = await eventService.getEvents({
        family_id: familyId,
        user_id: userId,
        task_type: 'event'
      });

      // Transform API data to match UI expectations
      const transformedEvents = fetchedEvents.map(event => ({
        task_id: event.task_id || event.id,
        title: event.title || 'Untitled Event',
        description: event.description || '',
        start_date: event.start_date,
        end_date: event.end_date,
        status: (event.status || 'pending').toLowerCase(),
        total_tasks: event.total_tasks || 0,
        completed_tasks: event.completed_tasks || 0,
        in_progress_tasks: event.in_progress_tasks || 0,
        completion_percentage: event.completion_percentage || 0,
        created_by: event.created_by,
        visibility: event.visibility || 'family'
      }));

      setEvents(transformedEvents);
      console.log('[Dashboard] Loaded events:', transformedEvents.length);
    } catch (error) {
      console.error('[Dashboard] Failed to load events:', error);
      toast.error('Failed to load events. Please try again.');
      setEvents([]);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    toast.success('Refreshing...', { icon: '🔄' });
    await Promise.all([loadTasks(), loadEvents(), loadWeatherData()]);
    setRefreshing(false);
    toast.success('Updated!', { icon: '✅' });
  };

  // Initialize location services
  const initializeLocationServices = async () => {
    try {
      // Request location permission
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            const coords = {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              accuracy: position.coords.accuracy
            };

            setLocationPermissionStatus('granted');

            // Try to get home location from service
            const homeLocation = locationIntelligenceService.getHomeLocation();
            if (homeLocation) {
              setUserLocation(homeLocation);
              await loadWeatherForLocation(homeLocation);
            } else {
              // Create location from current coordinates
              const currentLocation = {
                id: 'current',
                name: 'Current Location',
                address: 'Your current location',
                coordinates: coords,
                type: 'home',
                isVerified: false,
                confidence: 0.8
              };
              setUserLocation(currentLocation);
              await loadWeatherForLocation(currentLocation);

              // Suggest setting as home location
              toast(
                (t) => (
                  <div className="flex flex-col gap-2">
                    <span>Would you like to set this as your home location?</span>
                    <div className="flex gap-2">
                      <button
                        onClick={async () => {
                          await locationIntelligenceService.setHomeLocation(currentLocation);
                          toast.success('Home location saved!');
                          toast.dismiss(t.id);
                        }}
                        className="px-3 py-1 bg-purple-500 text-white rounded-lg text-sm"
                      >
                        Yes
                      </button>
                      <button
                        onClick={() => toast.dismiss(t.id)}
                        className="px-3 py-1 bg-gray-300 text-gray-700 rounded-lg text-sm"
                      >
                        Not now
                      </button>
                    </div>
                  </div>
                ),
                { duration: 8000 }
              );
            }
          },
          (error) => {
            console.warn('Geolocation error:', error);
            setLocationPermissionStatus('denied');

            // Still try to load weather for a default location if we have one saved
            const homeLocation = locationIntelligenceService.getHomeLocation();
            if (homeLocation) {
              setUserLocation(homeLocation);
              loadWeatherForLocation(homeLocation);
            }
          },
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 300000 // 5 minutes
          }
        );
      } else {
        console.warn('Geolocation not supported');
        setLocationPermissionStatus('denied');
      }
    } catch (error) {
      console.error('Failed to initialize location services:', error);
    }
  };

  // Load weather data for a location
  const loadWeatherForLocation = async (location) => {
    if (!location || !location.coordinates) return;

    setLoadingWeather(true);
    try {
      const forecast = await weatherSchedulingService.getWeatherForecast(
        undefined,
        location.coordinates.latitude,
        location.coordinates.longitude,
        7 // 7-day forecast
      );

      if (forecast && forecast.length > 0) {
        setWeatherData(forecast[0]); // Today's weather
        setWeatherForecast(forecast.slice(0, 4)); // Next 4 days for widget

        // Extract weather alerts
        const alerts = forecast.filter(day =>
          day.rainChance > 70 || day.tempHigh > 95 || day.tempLow < 32
        ).map(day => ({
          date: day.date,
          type: day.rainChance > 70 ? 'rain' : day.tempHigh > 95 ? 'heat' : 'cold',
          message: day.rainChance > 70
            ? `${day.rainChance}% chance of rain`
            : day.tempHigh > 95
            ? `High temperature: ${day.tempHigh}°F`
            : `Low temperature: ${day.tempLow}°F`
        }));
        setWeatherAlerts(alerts);
      }
    } catch (error) {
      console.error('Failed to load weather data:', error);
    } finally {
      setLoadingWeather(false);
    }
  };

  // Reload weather data (called by refresh)
  const loadWeatherData = async () => {
    if (userLocation) {
      await loadWeatherForLocation(userLocation);
    }
  };

  // Check weather suitability for a task
  const getTaskWeatherSuitability = useCallback((task) => {
    if (!weatherData || !task) return null;

    // Check if task is outdoor-related
    const outdoorCategories = ['landscaping', 'yard', 'exterior', 'gardening', 'outdoor'];
    const isOutdoor = outdoorCategories.includes(task.category?.toLowerCase());

    if (!isOutdoor) return null;

    // Calculate suitability score
    let score = 1.0;
    let warnings = [];

    if (weatherData.conditions === 'rainy' || weatherData.rainChance > 60) {
      score -= 0.5;
      warnings.push('Rain expected');
    }
    if (weatherData.tempHigh > 95) {
      score -= 0.3;
      warnings.push('Very hot');
    }
    if (weatherData.tempLow < 40) {
      score -= 0.2;
      warnings.push('Cold weather');
    }

    return {
      score,
      suitable: score > 0.5,
      warnings,
      icon: score > 0.7 ? '☀️' : score > 0.4 ? '⚠️' : '❌'
    };
  }, [weatherData]);

  // Calculate next due date based on frequency
  const calculateNextDueDate = (currentDate, frequency) => {
    const date = new Date(currentDate || Date.now());

    switch(frequency) {
      case 'daily':
        date.setDate(date.getDate() + 1);
        break;
      case 'weekly':
        date.setDate(date.getDate() + 7);
        break;
      case 'bi-weekly':
        date.setDate(date.getDate() + 14);
        break;
      case 'monthly':
        date.setMonth(date.getMonth() + 1);
        break;
      case 'quarterly':
        date.setMonth(date.getMonth() + 3);
        break;
      case 'yearly':
        date.setFullYear(date.getFullYear() + 1);
        break;
      default:
        date.setDate(date.getDate() + 7);
    }

    return date.toISOString().split('T')[0]; // YYYY-MM-DD format
  };

  // Complete recurring task and generate next instance
  const completeRecurringTask = (task) => {
    // Mark current task complete
    setTasks(prev => prev.map(t =>
      t.id === task.id ? { ...t, status: 'completed' } : t
    ));

    // Create next instance if recurring
    if (task.isRecurring) {
      const nextDueDate = calculateNextDueDate(task.dueDate, task.frequency);

      // Check if we should stop (if end date is set and passed)
      if (task.recurringEndDate) {
        const endDate = new Date(task.recurringEndDate);
        const nextDate = new Date(nextDueDate);
        if (nextDate > endDate) {
          toast.success(`Task completed! No more recurring instances.`);
          return;
        }
      }

      const nextTask = {
        ...task,
        id: Date.now(),
        status: 'pending',
        dueDate: nextDueDate
      };
      setTasks(prev => [nextTask, ...prev]);
      toast.success(`Task completed! Next instance created for ${nextDueDate}`, { icon: '🔄' });
    } else {
      toast.success(`Task "${task.title}" completed!`, { icon: '🎉' });
    }
  };

  const createTask = async () => {
    if (!newTask.title.trim()) {
      toast.error('Please enter a task title');
      return;
    }

    try {
      setLoading(true);

      // Prepare task data for API (lowercase snake_case for database)
      const taskData = {
        title: newTask.title,
        description: newTask.description,
        category: newTask.category,
        difficulty: 'medium', // Required field - default to medium
        priority_score: newTask.priority === 'urgent' ? 100 : newTask.priority === 'high' ? 75 : newTask.priority === 'medium' ? 50 : 25,
        due_date: newTask.dueDate || null,
        status: 'pending',
        family_id: familyId, // From user profile (database lookup after Cognito auth)
        // Note: created_by is determined by backend from JWT token (Tim-Combo pattern)
        is_recurring: newTask.isRecurring,
        frequency: newTask.frequency,
        recurring_end_date: newTask.recurringEndDate || null,
        task_type: newTask.task_type || 'task',
        visibility: newTask.visibility || 'family',
        private_notes: newTask.private_notes || null,
        reveal_date: newTask.reveal_date || null,
        event_id: newTask.event_id || null,
        parent_task_id: newTask.parent_task_id || null
      };

      console.log('[Dashboard] Creating task:', taskData);
      const createdTask = await taskService.createTask(taskData);

      // Transform response to match UI format
      const transformedTask = {
        id: createdTask.task_id || createdTask.id || Date.now(),
        title: createdTask.title || newTask.title,
        description: createdTask.description || newTask.description,
        priority: newTask.priority.toLowerCase(),
        category: (createdTask.category || newTask.category).toLowerCase(),
        status: 'pending',
        dueDate: createdTask.due_date || newTask.dueDate || 'No due date',
        assigned: createdTask.assigned_to_name || user?.displayName || 'You',
        createdBy: createdTask.created_by || userId, // Set createdBy for permission checks
        isRecurring: createdTask.is_recurring || newTask.isRecurring,
        frequency: createdTask.frequency || newTask.frequency,
        recurringEndDate: createdTask.recurring_end_date || newTask.recurringEndDate
      };

      setTasks(prev => [transformedTask, ...prev]);
      setShowAddTask(false);
      setNewTask({
        title: '',
        description: '',
        priority: 'medium',
        category: 'general',
        dueDate: '',
        location: '',
        isRecurring: false,
        frequency: 'weekly',
        recurringEndDate: '',
        task_type: 'task',
        visibility: 'family',
        private_notes: '',
        reveal_date: '',
        event_id: null,
        parent_task_id: null
      });

      if (newTask.isRecurring) {
        toast.success(`Recurring task created! Repeats ${newTask.frequency}`, { icon: '🔄' });
      } else {
        toast.success('Task created successfully! 🎉');
      }
    } catch (error) {
      console.error('[Dashboard] Failed to create task:', error);
      toast.error('Failed to create task. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const updateTask = async () => {
    if (!editedTask.title.trim()) {
      toast.error('Please enter a task title');
      return;
    }

    try {
      setLoading(true);

      // Prepare update data (lowercase snake_case for database)
      const updateData = {
        title: editedTask.title,
        description: editedTask.description,
        category: editedTask.category,
        priority_score: editedTask.priority === 'urgent' ? 100 : editedTask.priority === 'high' ? 75 : editedTask.priority === 'medium' ? 50 : 25,
        due_date: editedTask.dueDate && editedTask.dueDate !== 'No due date' ? editedTask.dueDate : null,
        status: editedTask.status
      };

      console.log('[Dashboard] Updating task:', editedTask.id, updateData);
      await taskService.updateTask(editedTask.id, updateData);

      setTasks(prev => prev.map(t =>
        t.id === editedTask.id ? editedTask : t
      ));
      setShowTaskDetail(false);
      setEditMode(false);
      setEditedTask(null);
      toast.success('Task updated successfully!', { icon: '✏️' });
    } catch (error) {
      console.error('[Dashboard] Failed to update task:', error);
      toast.error('Failed to update task. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const deleteTask = async (taskId) => {
    // Confirm deletion
    if (!window.confirm('Are you sure you want to delete this task? This action cannot be undone.')) {
      return;
    }

    try {
      setLoading(true);
      console.log('[Dashboard] Deleting task:', taskId);

      await taskService.deleteTask(taskId);

      // Remove task from UI
      setTasks(prev => prev.filter(t => t.id !== taskId));
      setShowTaskDetail(false);
      setEditMode(false);
      setSelectedTask(null);
      setEditedTask(null);

      toast.success('Task deleted successfully', { icon: '🗑️' });
    } catch (error) {
      console.error('[Dashboard] Failed to delete task:', error);
      toast.error('Failed to delete task. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Complete task with API persistence and optimistic updates
  const completeTaskAPI = async (task) => {
    // Optimistically update UI
    setTasks(prev => prev.map(t =>
      t.id === task.id ? { ...t, status: 'completed' } : t
    ));

    try {
      await taskService.updateTask(task.id, { status: 'completed' });
      toast.success(`Task "${task.title}" completed! 🎉`);
    } catch (error) {
      console.error('[Dashboard] Failed to complete task:', error);

      // Rollback on error
      setTasks(prev => prev.map(t =>
        t.id === task.id ? { ...t, status: 'pending' } : t
      ));
      toast.error('Failed to update task. Changes reverted.');
    }
  };

  // Delete task with API persistence and optimistic updates
  const deleteTaskAPI = async (task) => {
    // Optimistically remove from UI
    const previousTasks = [...tasks];
    setTasks(prev => prev.filter(t => t.id !== task.id));

    try {
      await taskService.deleteTask(task.id);
      toast.success(`Task "${task.title}" deleted`);
    } catch (error) {
      console.error('[Dashboard] Failed to delete task:', error);

      // Rollback on error
      setTasks(previousTasks);
      toast.error('Failed to delete task. Changes reverted.');
    }
  };

  // Family code generation utility (exclude confusing characters)
  const generateFamilyCode = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // No O/0, I/1
    let code = '';
    for (let i = 0; i < 6; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  };

  // Handle joining a family with a code
  const handleJoinFamily = async (code) => {
    if (code.length !== 6) {
      toast.error('Please enter a valid 6-character family code');
      return;
    }

    const loadingToast = toast.loading('Joining family...');

    try {
      // Call family join API
      const result = await familyService.joinFamilyByCode(code);

      if (result) {
        toast.success(`Successfully joined ${result.family_name}!`, { id: loadingToast });
        setJoinCode('');
        setShowInviteModal(false);

        // Reload family members to show updated list
        try {
          const members = await familyService.getMembers(result.family_id);
          setFamilyMembers(members.map(member => ({
            id: member.user_id,
            name: member.display_name,
            role: member.user_type,
            tasks: member.tasks_completed_count || 0,
            avatar: '👤'
          })));
        } catch (error) {
          console.error('[Dashboard] Failed to reload family members:', error);
        }
      }
    } catch (error) {
      console.error('[Dashboard] Failed to join family:', error);
      toast.error(error.message || 'Failed to join family. Please check the code and try again.', { id: loadingToast });
    }
  };

  // Handle sending email invite
  const handleEmailInvite = async () => {
    if (!inviteEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inviteEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }

    // Call API to send email invite using familyService
    const loadingToast = toast.loading('Sending invitation...');
    const emailToInvite = inviteEmail; // Save before clearing
    setInviteEmail(''); // Clear input immediately for better UX

    try {
      // Use familyService for consistent API calls
      await familyService.sendEmailInvite(emailToInvite, familyCode, 'HoneyDo Family');

      toast.success(`Invitation sent to ${emailToInvite}!`, { id: loadingToast });
      console.log('[Dashboard] Email invitation sent successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to send invitation', { id: loadingToast });
      console.error('[Dashboard] Email invitation error:', error);
    }
  };

  // Handle copying family code to clipboard
  const handleCopyCode = () => {
    navigator.clipboard.writeText(familyCode);
    toast.success('Family code copied to clipboard!', { icon: '📋' });
  };

  // Load tasks and events when familyId or userId changes
  useEffect(() => {
    if (familyId && userId) {
      (async () => {
        await Promise.all([loadTasks(), loadEvents()]);
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [familyId, userId]);

  // One-time initialization on mount
  useEffect(() => {
    // Generate or load family code
    const storedCode = localStorage.getItem('familyCode');
    if (storedCode) {
      setFamilyCode(storedCode);
    } else {
      const newCode = generateFamilyCode();
      setFamilyCode(newCode);
      localStorage.setItem('familyCode', newCode);
    }

    // Initialize location and weather services
    initializeLocationServices();

    // Family members will be loaded from API only - no mock data
  }, []);

  const stats = {
    active: tasks.filter(t => t.status === 'pending').length,
    completed: tasks.filter(t => t.status === 'completed').length,
    urgent: tasks.filter(t => t.priority === 'urgent').length,
    score: 85
  };

  const TabContent = () => {
    // Show loading state while fetching initial data
    if (loading && tasks.length === 0 && events.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-20">
          <Loader className="w-12 h-12 text-honey-500 animate-spin mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Loading your data...</p>
        </div>
      );
    }

    switch(activeTab) {
      case 'home':
        return (
          <HomeTab
            tasks={tasks}
            events={events}
            stats={stats}
            weatherData={weatherData}
            weatherForecast={weatherForecast}
            userLocation={userLocation}
            onTaskClick={(task) => {
              setSelectedTask(task);
              setShowTaskDetail(true);
            }}
            onEventClick={(event) => {
              setSelectedEvent(event);
              setShowEventDetail(true);
            }}
          />
        );
      case 'tasks':
        return (
          <TasksTab
            tasks={tasks}
            setTasks={setTasks}
            searchQuery={searchQuery}
            weatherData={weatherData}
            weatherForecast={weatherForecast}
            onTaskClick={(task) => {
              setSelectedTask(task);
              setShowTaskDetail(true);
            }}
            onComplete={completeTaskAPI}
            onDelete={deleteTaskAPI}
          />
        );
      case 'events':
        return (
          <EventsTab
            events={events}
            onEventClick={(event) => {
              setSelectedEvent(event);
              setShowEventDetail(true);
            }}
            onAddEvent={() => setShowAddEvent(true)}
          />
        );
      case 'family':
        return (
          <FamilyTab
            familyCode={familyCode}
            familyMembers={familyMembers}
            onInvite={() => setShowInviteModal(true)}
          />
        );
      default:
        return (
          <HomeTab
            tasks={tasks}
            events={events}
            stats={stats}
            weatherData={weatherData}
            weatherForecast={weatherForecast}
            userLocation={userLocation}
            onTaskClick={(task) => {
              setSelectedTask(task);
              setShowTaskDetail(true);
            }}
            onEventClick={(event) => {
              setSelectedEvent(event);
              setShowEventDetail(true);
            }}
          />
        );
    }
  };

  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''}`}>
      <div className="bg-gray-50 dark:bg-dark-bg min-h-screen pb-20">
        {/* Header */}
        <motion.header
          initial={{ y: -100 }}
          animate={{ y: 0 }}
          className="header px-4 py-4"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-honey rounded-xl flex items-center justify-center shadow-glow">
                <Home className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                  HoneyDo
                </h1>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Welcome back, {user?.displayName || 'Friend'}!
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={() => setDarkMode(!darkMode)}
                className="btn-icon"
              >
                {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </motion.button>

              {/* Notifications - Hidden until WebSocket API deployed
              <motion.button
                whileTap={{ scale: 0.95 }}
                className="btn-icon relative"
              >
                <Bell className="w-5 h-5" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full animate-pulse" />
              </motion.button>
              */}

              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={onLogout}
                className="btn-icon"
              >
                <LogOut className="w-5 h-5" />
              </motion.button>
            </div>
          </div>

          {/* Weather Widget */}
          {weatherData && userLocation && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-4 glass-card p-4 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {userLocation.name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {weatherData.conditions} • {weatherData.tempHigh}°F
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  {/* Today's Weather */}
                  <div className="text-center">
                    <div className="text-2xl">
                      {weatherData.conditions === 'sunny' ? '☀️' :
                       weatherData.conditions === 'rainy' ? '🌧️' :
                       weatherData.conditions === 'cloudy' ? '☁️' :
                       weatherData.conditions === 'snow' ? '❄️' : '🌤️'}
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Today</p>
                  </div>

                  {/* 3-Day Forecast */}
                  {weatherForecast.slice(1, 4).map((day, idx) => (
                    <div key={idx} className="text-center">
                      <div className="text-lg">
                        {day.conditions === 'sunny' ? '☀️' :
                         day.conditions === 'rainy' ? '🌧️' :
                         day.conditions === 'cloudy' ? '☁️' :
                         day.conditions === 'snow' ? '❄️' : '🌤️'}
                      </div>
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short' })}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{day.tempHigh}°</p>
                    </div>
                  ))}

                  {/* Change Location Button */}
                  <button
                    onClick={() => setShowLocationPicker(true)}
                    className="btn-icon ml-2"
                  >
                    <MapPin className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Weather Alerts */}
              {weatherAlerts.length > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-2 text-xs">
                    <AlertTriangle className="w-4 h-4 text-orange-500" />
                    <span className="text-gray-700 dark:text-gray-300">
                      {weatherAlerts[0].message} on {new Date(weatherAlerts[0].date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* Search Bar */}
          {activeTab === 'tasks' && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-4 relative"
            >
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search tasks..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input pl-12 pr-12"
              />
              <button className="absolute right-4 top-1/2 transform -translate-y-1/2">
                <Filter className="w-5 h-5 text-gray-400" />
              </button>
            </motion.div>
          )}
        </motion.header>

        {/* Main Content with Pull-to-Refresh */}
        <animated.div
          {...bind()}
          style={{ y }}
          className="px-4 py-2 overflow-y-auto custom-scrollbar"
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
            >
              <TabContent />
            </motion.div>
          </AnimatePresence>
        </animated.div>

        {/* Floating Action Button */}
        <motion.button
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setShowAddTask(true)}
          disabled={loading}
          className="fab"
        >
          {loading ? <Loader className="w-6 h-6 animate-spin" /> : <Plus className="w-6 h-6" />}
        </motion.button>

        {/* Add Task Modal */}
        <AnimatePresence>
          {showAddTask && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/50"
              onClick={() => setShowAddTask(false)}
            >
              <motion.div
                initial={{ scale: 0.9, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.9, y: 20 }}
                onClick={(e) => e.stopPropagation()}
                className="glass-card w-full max-w-md p-6"
              >
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                  Create New Task
                </h3>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Task Title
                    </label>
                    <input
                      type="text"
                      value={newTask.title}
                      onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                      placeholder="Enter task title..."
                      className="input"
                      autoFocus
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Description
                    </label>
                    <textarea
                      value={newTask.description}
                      onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                      placeholder="Enter task description..."
                      className="input min-h-[80px]"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Priority
                      </label>
                      <select
                        value={newTask.priority}
                        onChange={(e) => setNewTask({ ...newTask, priority: e.target.value })}
                        className="input"
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                        <option value="urgent">Urgent</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Category
                      </label>
                      <select
                        value={newTask.category}
                        onChange={(e) => setNewTask({ ...newTask, category: e.target.value })}
                        className="input"
                      >
                        <option value="general">General</option>
                        <option value="plumbing">Plumbing</option>
                        <option value="electrical">Electrical</option>
                        <option value="painting">Painting</option>
                        <option value="carpentry">Carpentry</option>
                        <option value="landscaping">Landscaping</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Due Date
                    </label>
                    <input
                      type="date"
                      value={newTask.dueDate}
                      onChange={(e) => setNewTask({ ...newTask, dueDate: e.target.value })}
                      className="input"
                    />
                  </div>

                  {/* Optional Location Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Location (Optional)
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newTask.location || ''}
                        onChange={(e) => setNewTask({ ...newTask, location: e.target.value })}
                        placeholder="Where will this task take place?"
                        className="input flex-1"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          // Set current location on task
                          if (userLocation) {
                            setNewTask({ ...newTask, location: userLocation.name });
                          }
                        }}
                        className="btn-secondary"
                        title="Use current location"
                      >
                        <MapPin className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Task Type and Visibility Section */}
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Task Type
                        </label>
                        <select
                          value={newTask.task_type}
                          onChange={(e) => setNewTask({ ...newTask, task_type: e.target.value })}
                          className="input"
                        >
                          <option value="task">Task</option>
                          <option value="subtask">Subtask</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Visibility
                        </label>
                        <select
                          value={newTask.visibility}
                          onChange={(e) => setNewTask({ ...newTask, visibility: e.target.value })}
                          className="input"
                        >
                          <option value="family">Family</option>
                          <option value="assigned_only">Assigned Only</option>
                          <option value="private">Private</option>
                          <option value="coordinators">Coordinators</option>
                          <option value="public">Public</option>
                        </select>
                      </div>
                    </div>

                    {/* Helper text for visibility */}
                    <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg mb-4">
                      <p className="text-xs text-blue-700 dark:text-blue-400">
                        {newTask.visibility === 'family' && 'Visible to all family members'}
                        {newTask.visibility === 'assigned_only' && 'Only visible to assigned users'}
                        {newTask.visibility === 'private' && 'Shows as "🔒 Personal Task" to others'}
                        {newTask.visibility === 'coordinators' && 'Only visible to event coordinators'}
                        {newTask.visibility === 'public' && 'Visible to everyone with the link'}
                      </p>
                    </div>

                    {/* Private Notes (only visible to creator) */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Private Notes (Only You Can See)
                      </label>
                      <textarea
                        value={newTask.private_notes}
                        onChange={(e) => setNewTask({ ...newTask, private_notes: e.target.value })}
                        placeholder="Add private notes visible only to you..."
                        className="input min-h-[60px]"
                      />
                    </div>

                    {/* Reveal Date (for surprise tasks like gifts) */}
                    {newTask.visibility === 'private' && (
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Reveal Date (Optional)
                        </label>
                        <input
                          type="date"
                          value={newTask.reveal_date}
                          onChange={(e) => setNewTask({ ...newTask, reveal_date: e.target.value })}
                          className="input"
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Task will become visible to others after this date (useful for surprises)
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Recurring Task Section */}
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                    <div className="flex items-center gap-3 mb-4">
                      <input
                        type="checkbox"
                        id="isRecurring"
                        checked={newTask.isRecurring}
                        onChange={(e) => setNewTask({ ...newTask, isRecurring: e.target.checked })}
                        className="w-4 h-4 text-honey-500 bg-gray-100 border-gray-300 rounded focus:ring-honey-500"
                      />
                      <label htmlFor="isRecurring" className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                        <Repeat className="w-4 h-4" />
                        Make this a recurring task
                      </label>
                    </div>

                    {newTask.isRecurring && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="space-y-4"
                      >
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Frequency
                          </label>
                          <select
                            value={newTask.frequency}
                            onChange={(e) => setNewTask({ ...newTask, frequency: e.target.value })}
                            className="input"
                          >
                            <option value="daily">Daily</option>
                            <option value="weekly">Weekly</option>
                            <option value="bi-weekly">Bi-weekly</option>
                            <option value="monthly">Monthly</option>
                            <option value="quarterly">Quarterly</option>
                            <option value="yearly">Yearly</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            End Date (Optional)
                          </label>
                          <input
                            type="date"
                            value={newTask.recurringEndDate}
                            onChange={(e) => setNewTask({ ...newTask, recurringEndDate: e.target.value })}
                            className="input"
                            placeholder="Leave empty for no end date"
                          />
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            Task will stop recurring after this date
                          </p>
                        </div>

                        {newTask.dueDate && (
                          <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
                            <p className="text-sm text-blue-700 dark:text-blue-400 font-medium">
                              Next Due Date: {calculateNextDueDate(newTask.dueDate, newTask.frequency)}
                            </p>
                          </div>
                        )}
                      </motion.div>
                    )}
                  </div>

                  <div className="flex gap-3 mt-6">
                    <button
                      onClick={() => setShowAddTask(false)}
                      className="btn-secondary flex-1"
                      disabled={loading}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={createTask}
                      className="btn-primary flex-1 flex items-center justify-center gap-2"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <Loader className="w-4 h-4 animate-spin" />
                          Creating...
                        </>
                      ) : (
                        <>
                          {newTask.isRecurring && <Repeat className="w-4 h-4" />}
                          Create Task
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Task Detail Modal */}
        <AnimatePresence>
          {showTaskDetail && selectedTask && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/50"
              onClick={() => setShowTaskDetail(false)}
            >
              <motion.div
                initial={{ scale: 0.9, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.9, y: 20 }}
                onClick={(e) => e.stopPropagation()}
                className="glass-card w-full max-w-md p-6 max-h-[90vh] overflow-y-auto"
              >
                <AnimatePresence mode="wait">
                  {!editMode ? (
                    // View Mode
                    <motion.div
                      key="view"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                    >
                      {/* Header */}
                      <div className="flex items-start justify-between mb-6">
                        <div className="flex-1">
                          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                            {selectedTask.title}
                          </h2>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`badge ${
                              selectedTask.priority === 'urgent' ? 'bg-red-500 text-white' :
                              selectedTask.priority === 'high' ? 'bg-orange-500 text-white' :
                              selectedTask.priority === 'medium' ? 'bg-blue-500 text-white' :
                              'bg-gray-500 text-white'
                            }`}>
                              {selectedTask.priority}
                            </span>
                            <span className="badge badge-info">
                              {selectedTask.category}
                            </span>
                            <span className={`badge ${
                              selectedTask.status === 'completed' ? 'bg-green-500 text-white' : 'bg-yellow-500 text-white'
                            }`}>
                              {selectedTask.status}
                            </span>
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            setShowTaskDetail(false);
                            setEditMode(false);
                          }}
                          className="btn-icon ml-2"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>

                      {/* Description */}
                      {selectedTask.description && (
                        <div className="mb-6">
                          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Description
                          </h3>
                          <p className="text-gray-600 dark:text-gray-400">
                            {selectedTask.description}
                          </p>
                        </div>
                      )}

                      {/* Details */}
                      <div className="space-y-4 mb-6">
                        <div className="flex items-center gap-3">
                          <Clock className="w-5 h-5 text-gray-400" />
                          <div>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Due Date</p>
                            <p className="text-gray-900 dark:text-white font-medium">
                              {selectedTask.dueDate}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-3">
                          <Users className="w-5 h-5 text-gray-400" />
                          <div>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Assigned To</p>
                            <p className="text-gray-900 dark:text-white font-medium">
                              {selectedTask.assigned}
                            </p>
                          </div>
                        </div>

                        {selectedTask.priority === 'urgent' && (
                          <div className="flex items-center gap-3 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                            <AlertCircle className="w-5 h-5 text-red-500" />
                            <div>
                              <p className="text-sm text-red-700 dark:text-red-400 font-medium">
                                This task requires immediate attention
                              </p>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Action Buttons */}
                      <div className="space-y-3">
                        {selectedTask.status !== 'completed' && (
                          <button
                            onClick={async () => {
                              await completeTaskAPI(selectedTask);
                              setShowTaskDetail(false);
                            }}
                            className="btn-primary w-full flex items-center justify-center gap-2"
                            disabled={loading}
                          >
                            {loading ? (
                              <>
                                <Loader className="w-5 h-5 animate-spin" />
                                Completing...
                              </>
                            ) : (
                              <>
                                <CheckCircle className="w-5 h-5" />
                                Mark as Complete
                              </>
                            )}
                          </button>
                        )}

                        <div className="flex gap-3">
                          <button
                            onClick={() => {
                              setEditMode(true);
                              setEditedTask({ ...selectedTask });
                            }}
                            className="btn-secondary flex-1 flex items-center justify-center gap-2"
                            disabled={loading}
                          >
                            <Edit2 className="w-4 h-4" />
                            Edit
                          </button>
                          <button
                            onClick={async () => {
                              await deleteTaskAPI(selectedTask);
                              setShowTaskDetail(false);
                            }}
                            className="btn-secondary flex-1 flex items-center justify-center gap-2 text-red-600 dark:text-red-400"
                            disabled={loading}
                          >
                            {loading ? (
                              <Loader className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <Trash2 className="w-4 h-4" />
                                Delete
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  ) : (
                    // Edit Mode
                    <motion.div
                      key="edit"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                    >
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                        Edit Task
                      </h3>

                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Task Title
                          </label>
                          <input
                            type="text"
                            value={editedTask.title}
                            onChange={(e) => setEditedTask({ ...editedTask, title: e.target.value })}
                            placeholder="Enter task title..."
                            className="input"
                            autoFocus
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Description
                          </label>
                          <textarea
                            value={editedTask.description || ''}
                            onChange={(e) => setEditedTask({ ...editedTask, description: e.target.value })}
                            placeholder="Enter task description..."
                            className="input min-h-[80px]"
                          />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Priority
                            </label>
                            <select
                              value={editedTask.priority}
                              onChange={(e) => setEditedTask({ ...editedTask, priority: e.target.value })}
                              className="input"
                            >
                              <option value="low">Low</option>
                              <option value="medium">Medium</option>
                              <option value="high">High</option>
                              <option value="urgent">Urgent</option>
                            </select>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Category
                            </label>
                            <select
                              value={editedTask.category}
                              onChange={(e) => setEditedTask({ ...editedTask, category: e.target.value })}
                              className="input"
                            >
                              <option value="general">General</option>
                              <option value="plumbing">Plumbing</option>
                              <option value="electrical">Electrical</option>
                              <option value="painting">Painting</option>
                              <option value="carpentry">Carpentry</option>
                              <option value="landscaping">Landscaping</option>
                            </select>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Due Date
                          </label>
                          <input
                            type="date"
                            value={editedTask.dueDate === 'No due date' || editedTask.dueDate === 'Today' ||
                                   editedTask.dueDate === 'Tomorrow' || editedTask.dueDate === 'Yesterday' ||
                                   editedTask.dueDate === 'This Week' ? '' : editedTask.dueDate}
                            onChange={(e) => setEditedTask({ ...editedTask, dueDate: e.target.value })}
                            className="input"
                          />
                        </div>

                        <div className="space-y-3 mt-6">
                          <div className="flex gap-3">
                            <button
                              onClick={() => {
                                setEditMode(false);
                                setEditedTask(null);
                              }}
                              className="btn-secondary flex-1"
                              disabled={loading}
                            >
                              Cancel
                            </button>
                            <button
                              onClick={updateTask}
                              className="btn-primary flex-1 flex items-center justify-center gap-2"
                              disabled={loading}
                            >
                              {loading ? (
                                <>
                                  <Loader className="w-4 h-4 animate-spin" />
                                  Saving...
                                </>
                              ) : (
                                'Save Changes'
                              )}
                            </button>
                          </div>

                          {/* Delete button - only visible to task creator */}
                          {(() => {
                            const canDelete = editedTask?.createdBy === userId;
                            console.log('[Dashboard] DELETE button check:', {
                              canDelete,
                              editedTaskCreatedBy: editedTask?.createdBy,
                              userId,
                              match: editedTask?.createdBy === userId
                            });
                            return canDelete ? (
                              <button
                                onClick={() => deleteTask(editedTask.id)}
                                className="w-full btn-secondary text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center justify-center gap-2"
                                disabled={loading}
                              >
                                <Trash2 className="w-4 h-4" />
                                Delete Task
                              </button>
                            ) : null;
                          })()}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Family Invite Modal */}
        <AnimatePresence>
          {showInviteModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/50"
              onClick={() => setShowInviteModal(false)}
            >
              <motion.div
                initial={{ scale: 0.9, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.9, y: 20 }}
                onClick={(e) => e.stopPropagation()}
                className="glass-card w-full max-w-md p-6 max-h-[90vh] overflow-y-auto"
              >
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Family Invites
                  </h2>
                  <button
                    onClick={() => setShowInviteModal(false)}
                    className="btn-icon"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Your Family Code Section */}
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    Your Family Code
                  </h3>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 bg-gradient-to-r from-honey-400 to-orange-500 text-white text-center py-4 rounded-xl shadow-lg">
                      <div className="text-3xl font-bold tracking-wider">{familyCode}</div>
                    </div>
                    <button
                      onClick={handleCopyCode}
                      className="btn-secondary flex-shrink-0"
                    >
                      <Copy className="w-5 h-5" />
                    </button>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                    Share this code with family members to invite them
                  </p>
                </div>

                {/* Email Invite Section */}
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    Send Email Invite
                  </h3>
                  <div className="flex gap-3">
                    <input
                      type="email"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      placeholder="family@example.com"
                      className="input flex-1"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleEmailInvite();
                        }
                      }}
                    />
                    <button
                      onClick={handleEmailInvite}
                      disabled={!inviteEmail.trim()}
                      className="btn-primary flex-shrink-0"
                    >
                      <Mail className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Join Family Section */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    Join a Family
                  </h3>
                  <div className="flex gap-3">
                    <input
                      type="text"
                      value={joinCode}
                      onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                      placeholder="Enter code"
                      maxLength={6}
                      className="input flex-1 text-center text-2xl tracking-wider font-bold"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && joinCode.length === 6) {
                          handleJoinFamily(joinCode);
                        }
                      }}
                    />
                    <button
                      onClick={() => handleJoinFamily(joinCode)}
                      disabled={joinCode.length !== 6}
                      className="btn-primary flex-shrink-0"
                    >
                      Join
                    </button>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                    Enter the 6-character code shared by another family
                  </p>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Event Creation Modal */}
        <EventCreationModal
          isOpen={showAddEvent}
          onClose={() => setShowAddEvent(false)}
          onEventCreated={async (newEvent) => {
            console.log('[Dashboard] Event created:', newEvent);
            // Reload events to show the new one
            await loadEvents();
          }}
          familyId={familyId}
          userId={userId}
        />

        {/* Location Picker Modal */}
        <LocationPicker
          isOpen={showLocationPicker}
          onClose={() => setShowLocationPicker(false)}
          onLocationSelect={(location) => {
            if (location) {
              setUserLocation(location);
              loadWeatherForLocation(location);
              toast.success(`Location set to ${location.name}`);
            }
          }}
          currentLocation={userLocation?.name}
          showWeatherInfo={true}
          allowCustomLocation={true}
        />

        {/* Bottom Navigation */}
        <motion.nav
          initial={{ y: 100 }}
          animate={{ y: 0 }}
          className="bottom-nav"
        >
          <div className="flex items-center justify-around py-2">
            <NavItem
              icon={Home}
              label="Home"
              active={activeTab === 'home'}
              onClick={() => setActiveTab('home')}
            />
            <NavItem
              icon={CheckCircle}
              label="Tasks"
              active={activeTab === 'tasks'}
              onClick={() => setActiveTab('tasks')}
              badge={stats.active}
            />
            <NavItem
              icon={Calendar}
              label="Events"
              active={activeTab === 'events'}
              onClick={() => setActiveTab('events')}
              badge={events.length}
            />
            <NavItem
              icon={Users}
              label="Family"
              active={activeTab === 'family'}
              onClick={() => setActiveTab('family')}
            />
            <NavItem
              icon={Settings}
              label="Settings"
              active={activeTab === 'settings'}
              onClick={() => navigate('/family-members')}
            />
          </div>
        </motion.nav>
      </div>
    </div>
  );
};

// Navigation Item Component
const NavItem = ({ icon: Icon, label, active, onClick, badge }) => (
  <motion.button
    whileTap={{ scale: 0.95 }}
    onClick={onClick}
    className={`nav-item ${active ? 'nav-item-active' : 'nav-item-inactive'}`}
  >
    <div className="relative">
      <Icon className="w-6 h-6" />
      {badge > 0 && (
        <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
          {badge}
        </span>
      )}
    </div>
    <span className="text-xs mt-1">{label}</span>
  </motion.button>
);

// Home Tab Component
const HomeTab = ({ tasks, stats, onTaskClick, weatherData, weatherForecast, userLocation }) => (
  <div className="space-y-6">
    {/* Welcome Card */}
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass-card p-6 bg-gradient-to-br from-honey-100 to-orange-50 dark:from-honey-900/20 dark:to-orange-900/20"
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Good {new Date().getHours() < 12 ? 'Morning' : new Date().getHours() < 18 ? 'Afternoon' : 'Evening'}! 👋
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            You have {stats.active} tasks to complete today
          </p>
        </div>
        <div className="text-4xl animate-bounce-slow">🏠</div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-4 gap-3 mt-6">
        <StatCard icon={Target} value={stats.active} label="Active" color="blue" />
        <StatCard icon={CheckCircle} value={stats.completed} label="Done" color="green" />
        <StatCard icon={Zap} value={stats.urgent} label="Urgent" color="red" />
        <StatCard icon={Award} value={`${stats.score}%`} label="Score" color="purple" />
      </div>
    </motion.div>

    {/* Weather Alerts */}
    {userLocation && (
      <WeatherAlerts
        tasks={tasks}
        location={{
          latitude: userLocation.coordinates?.latitude,
          longitude: userLocation.coordinates?.longitude
        }}
        onViewTasks={(taskIds) => {
          // Filter and highlight affected tasks
          console.log('[Dashboard] View tasks from weather alert:', taskIds);
        }}
        onReschedule={(taskIds) => {
          // Open reschedule modal for affected tasks
          console.log('[Dashboard] Reschedule tasks from weather alert:', taskIds);
        }}
      />
    )}

    {/* Weather-Based Task Suggestions */}
    {weatherData && weatherForecast.length > 0 && (
      <WeatherTaskSuggestions
        currentWeather={weatherData}
        forecast={weatherForecast}
        tasks={tasks}
        onTaskClick={onTaskClick}
        onTaskFilterClick={(category) => {
          // Filter tasks by category - future enhancement
          console.log('Filter tasks by category:', category);
        }}
      />
    )}

    {/* Upcoming Features Banner */}
    <UpcomingFeaturesBanner />

    {/* Recent Tasks */}
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Recent Tasks
        </h3>
        <button className="text-honey-500 hover:text-honey-600 text-sm font-medium flex items-center gap-1">
          View All <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      <div className="space-y-3">
        {tasks.slice(0, 3).map((task, index) => (
          <TaskCard
            key={task.id}
            task={task}
            index={index}
            onClick={() => onTaskClick(task)}
          />
        ))}
      </div>
    </div>

    {/* Weekly Progress */}
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
      className="glass-card p-6"
    >
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
        Weekly Progress
      </h3>
      <div className="space-y-3">
        <ProgressBar label="Completed" value={75} color="green" />
        <ProgressBar label="In Progress" value={20} color="yellow" />
        <ProgressBar label="Overdue" value={5} color="red" />
      </div>
    </motion.div>
  </div>
);

// Tasks Tab Component
const TasksTab = ({ tasks, setTasks, searchQuery, onTaskClick, onComplete, onDelete, weatherData, weatherForecast }) => {
  const filteredTasks = tasks.filter(task =>
    task.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (filteredTasks.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="text-6xl mb-4">📝</div>
        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
          {searchQuery ? 'No tasks found' : 'No tasks yet'}
        </h3>
        <p className="text-gray-600 dark:text-gray-400">
          {searchQuery ? 'Try a different search term' : 'Tap the + button to create your first task'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Weather-Based Task Suggestions - Only show if not searching */}
      {!searchQuery && weatherData && weatherForecast.length > 0 && (
        <WeatherTaskSuggestions
          currentWeather={weatherData}
          forecast={weatherForecast}
          tasks={tasks}
          onTaskClick={onTaskClick}
          onTaskFilterClick={(category) => {
            // Filter tasks by category - future enhancement
            console.log('Filter tasks by category:', category);
          }}
        />
      )}

      {filteredTasks.map((task, index) => (
        <SwipeableTaskCard
          key={task.id}
          task={task}
          index={index}
          onClick={() => onTaskClick(task)}
          onComplete={() => onComplete(task)}
          onDelete={() => onDelete(task)}
        />
      ))}
    </div>
  );
};

// Events Tab Component
const EventsTab = ({ events, onEventClick, onAddEvent }) => {
  if (events.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="text-6xl mb-4">📅</div>
        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
          No events yet
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          Create an event to organize and track multi-step projects
        </p>
        <button
          onClick={onAddEvent}
          className="btn-primary flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Create First Event
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
          Events
        </h2>
        <button
          onClick={onAddEvent}
          className="btn-secondary flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          New Event
        </button>
      </div>
      {events.map((event, index) => (
        <EventCard
          key={event.task_id}
          event={event}
          index={index}
          onClick={onEventClick}
        />
      ))}
    </div>
  );
};

// Family Tab Component
const FamilyTab = ({ familyCode, familyMembers, onInvite }) => (
  <div className="space-y-6">
    {/* Family Code Card */}
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass-card p-6 bg-gradient-to-br from-purple-100 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20"
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Family Code
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Share this code to invite members
          </p>
        </div>
        <div className="text-3xl">👨‍👩‍👧‍👦</div>
      </div>

      <div className="bg-white dark:bg-dark-card rounded-xl p-4 mb-4">
        <div className="text-center">
          <div className="text-3xl font-bold text-purple-600 dark:text-purple-400 tracking-wider">
            {familyCode}
          </div>
        </div>
      </div>

      <button
        onClick={onInvite}
        className="btn-primary w-full flex items-center justify-center gap-2"
      >
        <UserPlus className="w-5 h-5" />
        Invite Family Members
      </button>
    </motion.div>

    {/* Family Members List */}
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 }}
      className="glass-card p-6"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Family Members
        </h3>
        <span className="badge badge-info">
          {familyMembers.length} member{familyMembers.length !== 1 ? 's' : ''}
        </span>
      </div>

      {familyMembers.length > 0 ? (
        <div className="space-y-3">
          {familyMembers.map(member => (
            <FamilyMember
              key={member.id}
              name={member.name}
              role={member.role}
              tasks={member.tasks}
              avatar={member.avatar}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-8">
          <div className="text-6xl mb-4">👥</div>
          <h4 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">
            No Family Members Yet
          </h4>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Share your family code to invite members
          </p>
          <button
            onClick={onInvite}
            className="btn-secondary inline-flex items-center gap-2"
          >
            <UserPlus className="w-4 h-4" />
            Invite Members
          </button>
        </div>
      )}
    </motion.div>

    {/* Family Stats - Only show if there are members */}
    {familyMembers.length > 0 && (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="glass-card p-6"
      >
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Family Activity
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-xl p-4">
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
              {familyMembers.reduce((sum, m) => sum + m.tasks, 0)}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Total Tasks
            </div>
          </div>
          <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 rounded-xl p-4">
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">
              {Math.round((familyMembers.reduce((sum, m) => sum + m.tasks, 0) / familyMembers.length) * 10) / 10}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Avg per Member
            </div>
          </div>
        </div>
      </motion.div>
    )}
  </div>
);

// Helper Components
const StatCard = ({ icon: Icon, value, label, color }) => (
  <motion.div
    whileHover={{ scale: 1.05 }}
    className="glass rounded-xl p-3 text-center"
  >
    <Icon className={`w-5 h-5 mx-auto mb-1 text-${color}-500`} />
    <div className="text-lg font-bold text-gray-900 dark:text-white">{value}</div>
    <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
  </motion.div>
);

const TaskCard = ({ task, index, onClick }) => {
  // Get visibility icon
  const getVisibilityIcon = (visibility) => {
    switch (visibility) {
      case 'private':
        return <Lock className="w-4 h-4 text-red-500" title="Private" />;
      case 'assigned_only':
        return <EyeOff className="w-4 h-4 text-orange-500" title="Assigned Only" />;
      case 'coordinators':
        return <Crown className="w-4 h-4 text-purple-500" title="Coordinators Only" />;
      case 'public':
        return <Globe className="w-4 h-4 text-blue-500" title="Public" />;
      default:
        return <Eye className="w-4 h-4 text-gray-400" title="Family Visible" />;
    }
  };

  // Check if task is masked
  const isMasked = task.title === '🔒 Personal Task';

  // Get priority colors and styling
  const getPriorityStyle = (priority) => {
    switch(priority) {
      case 'urgent':
        return {
          borderColor: 'border-l-red-500',
          shadowColor: 'shadow-red-500/20',
          accentBg: 'bg-red-50 dark:bg-red-900/10',
          animation: 'animate-pulse'
        };
      case 'high':
        return {
          borderColor: 'border-l-orange-500',
          shadowColor: 'shadow-orange-500/20',
          accentBg: 'bg-orange-50 dark:bg-orange-900/10',
          animation: ''
        };
      case 'medium':
        return {
          borderColor: 'border-l-blue-500',
          shadowColor: 'shadow-blue-500/20',
          accentBg: 'bg-blue-50 dark:bg-blue-900/10',
          animation: ''
        };
      default:
        return {
          borderColor: 'border-l-gray-400',
          shadowColor: 'shadow-gray-500/20',
          accentBg: 'bg-gray-50 dark:bg-gray-900/10',
          animation: ''
        };
    }
  };

  const priorityStyle = getPriorityStyle(task.priority);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{
        delay: index * 0.1,
        type: "spring",
        stiffness: 260,
        damping: 20
      }}
      whileHover={{
        scale: 1.02,
        y: -4,
        transition: { duration: 0.2 }
      }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className={`
        relative cursor-pointer
        glass-card p-5
        border-l-4 ${priorityStyle.borderColor}
        shadow-lg ${priorityStyle.shadowColor}
        ${priorityStyle.animation}
        hover:shadow-xl
        transition-all duration-200
      `}
      style={{
        transformStyle: 'preserve-3d',
        transform: `translateZ(${index * 2}px)`,
      }}
    >
      {/* Priority indicator glow */}
      <div className={`absolute inset-0 ${priorityStyle.accentBg} rounded-2xl opacity-50 -z-10`} />

      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          {/* Task Title - Large and Prominent */}
          <div className="flex items-center gap-2 mb-2">
            <h3 className={`text-xl font-bold text-gray-900 dark:text-white line-clamp-2 ${isMasked ? 'italic' : ''}`}>
              {isMasked ? (
                <span className="flex items-center gap-2">
                  <Lock className="w-5 h-5" />
                  Personal Task
                </span>
              ) : (
                task.title
              )}
            </h3>
            {/* Privacy indicator */}
            {!isMasked && task.visibility && (
              <div className="flex-shrink-0">
                {getVisibilityIcon(task.visibility)}
              </div>
            )}
          </div>

          {/* Task Metadata */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`
              px-3 py-1 rounded-full text-xs font-semibold
              ${task.priority === 'urgent' ? 'bg-red-500 text-white' :
                task.priority === 'high' ? 'bg-orange-500 text-white' :
                task.priority === 'medium' ? 'bg-blue-500 text-white' :
                'bg-gray-500 text-white'}
            `}>
              {task.priority.toUpperCase()}
            </span>
            <span className="badge badge-info">
              {task.category}
            </span>

            {/* Weather Suitability Badge - Temporarily disabled due to scope issue */}

            {/* Hierarchy badges */}
            {task.event_id && (
              <span className="px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded text-xs font-medium">
                Event Task
              </span>
            )}
            {task.parent_task_id && (
              <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded text-xs font-medium">
                Subtask
              </span>
            )}
            {task.task_type === 'event' && (
              <span className="px-2 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded text-xs font-medium">
                Event
              </span>
            )}
          </div>

          {/* Due Date, Assignment & Location */}
          <div className="flex items-center gap-4 mt-3 text-sm text-gray-600 dark:text-gray-400">
            <div className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              <span>{task.dueDate}</span>
            </div>
            <div className="flex items-center gap-1">
              <Users className="w-4 h-4" />
              <span>{task.assigned}</span>
            </div>
            {task.location && (
              <div className="flex items-center gap-1">
                <MapPin className="w-4 h-4 text-purple-500" />
                <span>{task.location}</span>
              </div>
            )}
          </div>
        </div>

        {/* Chevron Indicator */}
        <motion.div
          whileHover={{ x: 4 }}
          className="flex-shrink-0"
        >
          <ChevronRight className="w-6 h-6 text-gray-400" />
        </motion.div>
      </div>

      {/* Task status indicator */}
      {task.status === 'completed' && (
        <div className="absolute top-4 right-4">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="bg-green-500 rounded-full p-1"
          >
            <CheckCircle className="w-5 h-5 text-white" />
          </motion.div>
        </div>
      )}

      {/* Subtask list - only show for parent tasks (not subtasks themselves) */}
      {task.id && !task.parent_task_id && (
        <SubtaskList
          parentTaskId={task.id}
          onSubtaskUpdate={() => {
            // Refresh tasks when subtasks change
            console.log('[TaskCard] Subtask updated, consider refreshing parent task');
          }}
        />
      )}
    </motion.div>
  );
};

const SwipeableTaskCard = ({ task, index, onComplete, onDelete, onClick }) => {
  const [{ x }, api] = useSpring(() => ({ x: 0 }));

  // Get visibility icon
  const getVisibilityIcon = (visibility) => {
    switch (visibility) {
      case 'private':
        return <Lock className="w-4 h-4 text-red-500" title="Private" />;
      case 'assigned_only':
        return <EyeOff className="w-4 h-4 text-orange-500" title="Assigned Only" />;
      case 'coordinators':
        return <Crown className="w-4 h-4 text-purple-500" title="Coordinators Only" />;
      case 'public':
        return <Globe className="w-4 h-4 text-blue-500" title="Public" />;
      default:
        return <Eye className="w-4 h-4 text-gray-400" title="Family Visible" />;
    }
  };

  // Check if task is masked
  const isMasked = task.title === '🔒 Personal Task';

  const bind = useDrag(
    ({ down, movement: [mx], velocity, direction: [xDir], tap }) => {
      const trigger = velocity[0] > 0.2;

      // Handle tap to open detail
      if (tap && onClick) {
        onClick();
        return;
      }

      if (!down && trigger && xDir > 0) {
        onComplete();
      } else if (!down && trigger && xDir < 0) {
        onDelete();
      }

      api.start({
        x: down ? mx : 0,
        immediate: down
      });
    },
    { axis: 'x' }
  );

  // Get priority colors and styling
  const getPriorityStyle = (priority) => {
    switch(priority) {
      case 'urgent':
        return {
          borderColor: 'border-l-red-500',
          shadowColor: 'shadow-red-500/20',
          accentBg: 'bg-red-50 dark:bg-red-900/10',
          animation: 'animate-pulse'
        };
      case 'high':
        return {
          borderColor: 'border-l-orange-500',
          shadowColor: 'shadow-orange-500/20',
          accentBg: 'bg-orange-50 dark:bg-orange-900/10',
          animation: ''
        };
      case 'medium':
        return {
          borderColor: 'border-l-blue-500',
          shadowColor: 'shadow-blue-500/20',
          accentBg: 'bg-blue-50 dark:bg-blue-900/10',
          animation: ''
        };
      default:
        return {
          borderColor: 'border-l-gray-400',
          shadowColor: 'shadow-gray-500/20',
          accentBg: 'bg-gray-50 dark:bg-gray-900/10',
          animation: ''
        };
    }
  };

  const priorityStyle = getPriorityStyle(task.priority);

  return (
    <div className="relative">
      {/* Swipe action backgrounds */}
      <div className="absolute inset-0 flex items-center justify-between px-6 rounded-2xl overflow-hidden">
        <div className="flex items-center gap-2 text-green-600">
          <CheckCircle className="w-6 h-6" />
          <span className="font-semibold">Complete</span>
        </div>
        <div className="flex items-center gap-2 text-red-600">
          <span className="font-semibold">Delete</span>
          <Trash2 className="w-6 h-6" />
        </div>
      </div>

      {/* Main card */}
      <animated.div
        {...bind()}
        style={{ x }}
        className={`
          relative
          glass-card p-5
          border-l-4 ${priorityStyle.borderColor}
          shadow-lg ${priorityStyle.shadowColor}
          ${priorityStyle.animation}
          hover:shadow-xl
          cursor-grab active:cursor-grabbing
          transition-shadow duration-200
        `}
      >
        {/* Priority indicator glow */}
        <div className={`absolute inset-0 ${priorityStyle.accentBg} rounded-2xl opacity-50 -z-10`} />

        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            {/* Task Title - Large and Prominent */}
            <div className="flex items-center gap-2 mb-2">
              <h3 className={`text-xl font-bold text-gray-900 dark:text-white line-clamp-2 ${isMasked ? 'italic' : ''}`}>
                {isMasked ? (
                  <span className="flex items-center gap-2">
                    <Lock className="w-5 h-5" />
                    Personal Task
                  </span>
                ) : (
                  task.title
                )}
              </h3>
              {/* Privacy indicator */}
              {!isMasked && task.visibility && (
                <div className="flex-shrink-0">
                  {getVisibilityIcon(task.visibility)}
                </div>
              )}
            </div>

            {/* Task Metadata */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`
                px-3 py-1 rounded-full text-xs font-semibold
                ${task.priority === 'urgent' ? 'bg-red-500 text-white' :
                  task.priority === 'high' ? 'bg-orange-500 text-white' :
                  task.priority === 'medium' ? 'bg-blue-500 text-white' :
                  'bg-gray-500 text-white'}
              `}>
                {task.priority.toUpperCase()}
              </span>
              <span className="badge badge-info">
                {task.category}
              </span>

              {/* Weather Suitability Badge - Temporarily disabled due to scope issue */}

              {/* Hierarchy badges */}
              {task.event_id && (
                <span className="px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded text-xs font-medium">
                  Event Task
                </span>
              )}
              {task.parent_task_id && (
                <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded text-xs font-medium">
                  Subtask
                </span>
              )}
              {task.task_type === 'event' && (
                <span className="px-2 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded text-xs font-medium">
                  Event
                </span>
              )}
            </div>

            {/* Due Date, Assignment & Location */}
            <div className="flex items-center gap-4 mt-3 text-sm text-gray-600 dark:text-gray-400">
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                <span>{task.dueDate}</span>
              </div>
              <div className="flex items-center gap-1">
                <Users className="w-4 h-4" />
                <span>{task.assigned}</span>
              </div>
              {task.location && (
                <div className="flex items-center gap-1">
                  <MapPin className="w-4 h-4 text-purple-500" />
                  <span>{task.location}</span>
                </div>
              )}
            </div>
          </div>

          {/* Swipe hint */}
          <div className="flex-shrink-0 text-gray-400">
            <div className="flex gap-1">
              <ChevronRight className="w-4 h-4 animate-pulse" />
              <ChevronRight className="w-4 h-4 animate-pulse" style={{ animationDelay: '0.1s' }} />
            </div>
          </div>
        </div>

        {/* Task status indicator */}
        {task.status === 'completed' && (
          <div className="absolute top-4 right-4">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="bg-green-500 rounded-full p-1"
            >
              <CheckCircle className="w-5 h-5 text-white" />
            </motion.div>
          </div>
        )}

        {/* Subtask list - only show for parent tasks (not subtasks themselves) */}
        {task.id && !task.parent_task_id && (
          <SubtaskList
            parentTaskId={task.id}
            onSubtaskUpdate={() => {
              // Refresh tasks when subtasks change
              console.log('[SwipeableTaskCard] Subtask updated, consider refreshing parent task');
            }}
          />
        )}
      </animated.div>
    </div>
  );
};

const ProgressBar = ({ label, value, color }) => (
  <div>
    <div className="flex justify-between mb-1">
      <span className="text-sm text-gray-600 dark:text-gray-400">{label}</span>
      <span className="text-sm font-medium text-gray-900 dark:text-white">{value}%</span>
    </div>
    <div className="w-full bg-gray-200 dark:bg-dark-card rounded-full h-2">
      <motion.div
        initial={{ width: 0 }}
        animate={{ width: `${value}%` }}
        transition={{ duration: 1, ease: "easeOut" }}
        className={`bg-${color}-500 h-2 rounded-full`}
      />
    </div>
  </div>
);

const FamilyMember = ({ name, role, tasks, avatar }) => (
  <motion.div
    whileHover={{ scale: 1.02 }}
    className="flex items-center justify-between p-3 bg-gray-50 dark:bg-dark-card rounded-xl"
  >
    <div className="flex items-center gap-3">
      <div className="text-2xl">{avatar}</div>
      <div>
        <div className="font-medium text-gray-900 dark:text-white">{name}</div>
        <div className="text-xs text-gray-500 dark:text-gray-400">{role}</div>
      </div>
    </div>
    <div className="text-right">
      <div className="text-sm font-medium text-gray-900 dark:text-white">{tasks} tasks</div>
      <div className="text-xs text-gray-500 dark:text-gray-400">this week</div>
    </div>
  </motion.div>
);

export default ModernDashboard;